import { useState, FormEvent } from 'react';

interface EmergencyModalProps {
  onClose: () => void;
}

export default function EmergencyModal({ onClose }: EmergencyModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    urgency: 'Emergency — Need help now',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const SLACK_WEBHOOK_URL = 'YOUR_SLACK_WEBHOOK_URL';

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const slackPayload = {
        text: `🚨 *EMERGENCY REQUEST*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone}\n*Urgency:* ${formData.urgency}\n*Message:* ${formData.message}`,
      };

      const [readyResponse] = await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d6ontcl39lnhn4hh1e30', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        fetch(SLACK_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(slackPayload),
        }),
      ]);

      if (readyResponse.status === 'fulfilled' && readyResponse.value.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', urgency: 'Emergency — Need help now', message: '' });
        setTimeout(() => {
          setFormStatus('idle');
          onClose();
        }, 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#1a1a1a] text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <i className="ri-alarm-warning-fill text-[#FFD700] text-3xl"></i>
            <div>
              <h2 className="text-2xl font-black">24/7 EMERGENCY SERVICE</h2>
              <p className="text-gray-300 text-sm">We'll respond immediately</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8" data-readdy-form id="emergency-modal-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="emergency-name" className="block text-sm font-bold text-[#1a1a1a] mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="emergency-name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="emergency-phone" className="block text-sm font-bold text-[#1a1a1a] mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="emergency-phone"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
                placeholder="(559) 555-1234"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="emergency-email" className="block text-sm font-bold text-[#1a1a1a] mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="emergency-email"
              name="email"
              value={formData.email}
              onChange={handleFormChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors"
              placeholder="john@example.com"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="emergency-urgency" className="block text-sm font-bold text-[#1a1a1a] mb-2">
              Urgency *
            </label>
            <select
              id="emergency-urgency"
              name="urgency"
              value={formData.urgency}
              onChange={handleFormChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors bg-white cursor-pointer"
            >
              <option value="Emergency — Need help now">🚨 Emergency — Need help now</option>
              <option value="Urgent — Within 24 hours">⚡ Urgent — Within 24 hours</option>
              <option value="Soon — Within a few days">📅 Soon — Within a few days</option>
              <option value="Planning — Just getting a quote">💬 Planning — Just getting a quote</option>
            </select>
          </div>

          <div className="mb-6">
            <label htmlFor="emergency-message" className="block text-sm font-bold text-[#1a1a1a] mb-2">
              Describe Your Emergency *
            </label>
            <textarea
              id="emergency-message"
              name="message"
              value={formData.message}
              onChange={handleFormChange}
              maxLength={500}
              rows={4}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm focus:border-[#FFD700] focus:outline-none transition-colors resize-none"
              placeholder="Tell us what's happening..."
            ></textarea>
          </div>

          {formStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
              <span className="text-sm font-medium text-green-700">
                Emergency request received! We'll contact you immediately.
              </span>
            </div>
          )}

          {formStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-fill text-red-500 text-xl"></i>
              <span className="text-sm font-medium text-red-700">
                Something went wrong. Please try again.
              </span>
            </div>
          )}

          <button
            type="submit"
            disabled={formStatus === 'sending'}
            className="w-full bg-[#FFD700] text-[#1a1a1a] py-4 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-[#FFC700] transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            {formStatus === 'sending' ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Sending...
              </>
            ) : (
              <>
                <i className="ri-alarm-warning-fill"></i>
                Request Emergency Service
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
